import { toCsv } from "@/lib/csv/writer";
import type { QualityIssue } from "@/types";
import { FOOTER_TEXT } from "@/lib/branding";

/**
 * Browser ports of the Python quality-report writers (`reports/csv_writer.py`,
 * `json_writer.py`, `html_writer.py` + `templates/report.html.jinja`). Output is
 * byte-compatible in structure so the web and CLI reports match.
 */

export interface ReportSummary {
  total: number;
  byRule: Record<string, number>;
  bySeverity: Record<string, number>;
  byCategory: Record<string, number>;
}

/** Aggregate issues into per-rule, per-severity, and per-category counts. */
export function summarizeIssues(issues: readonly QualityIssue[]): ReportSummary {
  const byRule: Record<string, number> = {};
  const bySeverity: Record<string, number> = {};
  const byCategory: Record<string, number> = {};
  for (const issue of issues) {
    byRule[issue.rule_id] = (byRule[issue.rule_id] ?? 0) + 1;
    bySeverity[issue.severity] = (bySeverity[issue.severity] ?? 0) + 1;
    byCategory[issue.category] = (byCategory[issue.category] ?? 0) + 1;
  }
  return { total: issues.length, byRule, bySeverity, byCategory };
}

/** Stable JSON of a details object with sorted keys (matches Python `sort_keys=True`). */
function sortedDetailsJson(details: Record<string, unknown>): string {
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(details).sort()) {
    sorted[key] = details[key];
  }
  return JSON.stringify(sorted);
}

const CSV_COLUMNS = ["rule_id", "severity", "product_id", "category", "message", "details_json"];

/** Render the quality report as CSV (mirror of `_DATA_ISSUES.csv`). */
export function qualityReportCsv(issues: readonly QualityIssue[]): string {
  const records = issues.map((issue) => ({
    rule_id: issue.rule_id,
    severity: issue.severity,
    product_id: issue.product_id,
    category: issue.category,
    message: issue.message,
    details_json: sortedDetailsJson(issue.details),
  }));
  return toCsv(records, CSV_COLUMNS);
}

/** Render the quality report as JSON (mirror of `_DATA_ISSUES.json`). */
export function qualityReportJson(issues: readonly QualityIssue[]): string {
  const summary = summarizeIssues(issues);
  const document = {
    summary: {
      total: summary.total,
      by_rule: summary.byRule,
      by_severity: summary.bySeverity,
      by_category: summary.byCategory,
    },
    issues: issues.map((issue) => ({
      rule_id: issue.rule_id,
      severity: issue.severity,
      product_id: issue.product_id,
      category: issue.category,
      message: issue.message,
      details: issue.details,
    })),
  };
  return JSON.stringify(document, null, 2);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export interface HtmlReportOptions {
  title?: string;
  generatedAt?: string;
}

const REPORT_STYLES = `
  :root {
    --bg: #f8fafc; --card: #ffffff; --border: #e2e8f0; --text: #0f172a;
    --muted: #64748b; --accent: #3b82f6; --warning-bg: #fef3c7;
    --warning-text: #92400e; --info-bg: #dbeafe; --info-text: #1e40af;
  }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: var(--text); background: var(--bg); margin: 0; padding: 2rem 1rem; line-height: 1.5; }
  .container { max-width: 1100px; margin: 0 auto; }
  header { margin-bottom: 1.5rem; }
  h1 { font-size: 1.5rem; margin: 0 0 0.25rem; }
  h2 { font-size: 1.15rem; margin: 2rem 0 0.75rem; }
  .muted { color: var(--muted); font-size: 0.9rem; }
  .cards { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 1rem; }
  .card { background: var(--card); border: 1px solid var(--border); border-radius: 10px;
    padding: 0.9rem 1.1rem; min-width: 120px; }
  .card .value { font-size: 1.6rem; font-weight: 700; }
  .card .label { color: var(--muted); font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em; }
  .card.accent { border-left: 4px solid var(--accent); }
  table { width: 100%; border-collapse: collapse; background: var(--card); border: 1px solid var(--border);
    border-radius: 10px; overflow: hidden; font-size: 0.9rem; }
  th, td { text-align: left; padding: 0.55rem 0.75rem; border-bottom: 1px solid var(--border); }
  th { background: #f1f5f9; font-weight: 600; }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:nth-child(even) { background: #fafcff; }
  code { font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.82em; color: var(--muted); word-break: break-word; }
  .badge { display: inline-block; padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600; }
  .badge.warning { background: var(--warning-bg); color: var(--warning-text); }
  .badge.info { background: var(--info-bg); color: var(--info-text); }
  .rule-id { font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; }
  .empty { background: var(--card); border: 1px solid var(--border); border-radius: 10px;
    padding: 1.5rem; text-align: center; color: var(--muted); }
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
  @media (max-width: 720px) { .two-col { grid-template-columns: 1fr; } }
  @media print { body { background: #fff; padding: 0; } .card, table { border-color: #cbd5e1; }
    h2 { page-break-after: avoid; } tr { page-break-inside: avoid; } }
`;

/** Render a self-contained HTML quality report (port of the Jinja template). */
export function qualityReportHtml(
  issues: readonly QualityIssue[],
  options: HtmlReportOptions = {},
): string {
  const title = options.title ?? "CatalogKit Data Quality Report";
  const generatedAt = options.generatedAt ?? new Date().toLocaleString();
  const summary = summarizeIssues(issues);

  const severityCards = Object.entries(summary.bySeverity)
    .map(
      ([severity, count]) =>
        `<div class="card"><div class="value">${count}</div><div class="label">${escapeHtml(severity)}</div></div>`,
    )
    .join("");

  let body: string;
  if (summary.total === 0) {
    body = `<div class="empty" style="margin-top: 1.5rem;">No data-quality issues detected.</div>`;
  } else {
    const ruleRows = Object.entries(summary.byRule)
      .map(([rule, count]) => `<tr><td class="rule-id">${escapeHtml(rule)}</td><td>${count}</td></tr>`)
      .join("");
    const categoryRows = Object.entries(summary.byCategory)
      .map(
        ([category, count]) =>
          `<tr><td>${category ? escapeHtml(category) : "(uncategorized)"}</td><td>${count}</td></tr>`,
      )
      .join("");
    const detailRows = issues
      .map(
        (issue) =>
          `<tr><td class="rule-id">${escapeHtml(issue.rule_id)}</td>` +
          `<td><span class="badge ${escapeHtml(issue.severity)}">${escapeHtml(issue.severity)}</span></td>` +
          `<td>${escapeHtml(issue.product_id)}</td><td>${escapeHtml(issue.category)}</td>` +
          `<td>${escapeHtml(issue.message)}</td>` +
          `<td><code>${escapeHtml(sortedDetailsJson(issue.details))}</code></td></tr>`,
      )
      .join("");
    body =
      `<div class="two-col">` +
      `<section><h2>Issues by rule</h2><table><thead><tr><th>Rule</th><th>Count</th></tr></thead><tbody>${ruleRows}</tbody></table></section>` +
      `<section><h2>Issues by category</h2><table><thead><tr><th>Category</th><th>Count</th></tr></thead><tbody>${categoryRows}</tbody></table></section>` +
      `</div>` +
      `<section><h2>Full detail (${summary.total} issues)</h2><table><thead><tr>` +
      `<th>Rule</th><th>Severity</th><th>Product ID</th><th>Category</th><th>Message</th><th>Details</th>` +
      `</tr></thead><tbody>${detailRows}</tbody></table></section>`;
  }

  return (
    `<!doctype html>\n<html lang="en">\n<head>\n<meta charset="utf-8" />\n` +
    `<meta name="viewport" content="width=device-width, initial-scale=1" />\n` +
    `<title>${escapeHtml(title)}</title>\n<style>${REPORT_STYLES}</style>\n</head>\n<body>\n` +
    `<div class="container">\n<header><h1>${escapeHtml(title)}</h1>` +
    `<div class="muted">Generated ${escapeHtml(generatedAt)}</div></header>\n` +
    `<section aria-label="Summary statistics"><div class="cards">` +
    `<div class="card accent"><div class="value">${summary.total}</div><div class="label">Total issues</div></div>` +
    `${severityCards}</div></section>\n${body}\n<footer class="muted" style="margin-top:2rem;text-align:center">${escapeHtml(FOOTER_TEXT)}</footer>\n</div>\n</body>\n</html>\n`
  );
}
